import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getFontWeight } from '../../helpers/utils';

const SignatureElement = ({
  name,
  is_required = false,
  value,
  lexicon,
  onChange = () => {},
  className = '',
  error,
  disabled = false,
  attributes,
  font,
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // If there's a value (base64 image), draw it on canvas
    if (value) {
      // Handle both data URL format and raw base64 string
      let imageSrc = value;
      if (!value.startsWith('data:image/')) {
        // If it's raw base64, convert to data URL
        imageSrc = `data:image/png;base64,${value}`;
      }

      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasSignature(true);
      };
      img.onerror = () => {
        console.error('Failed to load signature image');
        setHasSignature(false);
      };
      img.src = imageSrc;
    } else {
      setHasSignature(false);
    }
  }, [value]);

  const getCoordinates = e => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  const startDrawing = e => {
    if (disabled) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = e => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL('image/png');
      onChange(lexicon, dataURL);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
      onChange(lexicon, '');
    }
  };

  return (
    <SignatureElementWrapper className={className}>
      {name && (
        <div className="mb-1" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', width: '100%' }}>
          <span
            style={{
              color: attributes?.font?.color || font?.color,
              fontSize: attributes?.font?.size || font?.size,
              fontWeight: getFontWeight(attributes?.font?.style || font?.style),
              fontFamily: attributes?.font?.name || font?.name,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              display: 'block',
            }}>
            {name}
          </span>
          {is_required && <span className="required-star">!</span>}
        </div>
      )}
      <SignatureCanvasWrapper>
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{
            border: error ? '1px solid #EF4444' : '1px solid #ddd',
            borderRadius: '6px',
            cursor: disabled ? 'not-allowed' : 'crosshair',
            touchAction: 'none',
            width: '100%',
            height: '200px',
            backgroundColor: '#fff',
          }}
        />
        {hasSignature && !disabled && (
          <ClearButton type="button" onClick={clearSignature}>
            Clear
          </ClearButton>
        )}
      </SignatureCanvasWrapper>
    </SignatureElementWrapper>
  );
};

const SignatureElementWrapper = styled('div')`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const SignatureCanvasWrapper = styled('div')`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const ClearButton = styled('button')`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  z-index: 10;

  &:hover {
    background-color: #e5e5e5;
  }

  &:active {
    background-color: #d5d5d5;
  }
`;

export default SignatureElement;
